import axiosInstance from '../axiosInstance';
import { URL, JOB_STORE, TRAINER_STORE, DAILY_LOG_STORE } from '../config/constants';
import { initializeDB } from '../config/db';

const STORE_NAME = DAILY_LOG_STORE || 'dailyLogs';

// Initialize IndexedDB
const initDB = async () => {
  // Use the main initializeDB to ensure all stores are created
  return initializeDB();
};

const dailyLogService = {
  // Get current user's log for today
  getCurrentUserLog: async () => {
    try {
      const db = await initDB();
      const userId = localStorage.getItem('userId');
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        console.log('DailyLogs store does not exist yet');
        return null;
      }
      
      const getTodayDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const today = getTodayDate();
      const allLogs = await db.getAll(STORE_NAME);
      
      console.log('Looking for daily log:', { userId, today, totalLogs: allLogs.length });
      console.log('All logs in IndexedDB:', allLogs);
      
      // Find log for current user and today
      // Check both personId (for local logs) and createdBy.id (for server logs)
      const userLog = allLogs.find(log => {
        const logDate = log.date ? log.date.split('T')[0] : null;
        const isToday = logDate === today;
        const isDeleted = log.deleted;
        
        // Check if log belongs to current user - compare as strings to avoid type issues
        const isUserLog = log.personId?.toString() === userId || 
                         (log.createdBy && log.createdBy.id && log.createdBy.id.toString() === userId) ||
                         log.createdBy?.toString() === userId || // sometimes createdBy is just an ID
                         log.id?.toString() === userId; // sometimes the log id matches userId (weird but check it)
        
        console.log('Checking log:', { 
          localId: log.localId,
          logDate, 
          isToday, 
          isUserLog, 
          isDeleted,
          personId: log.personId, 
          createdById: log.createdBy?.id || log.createdBy,
          userId,
          hourlyDataKeys: log.hourlyData ? Object.keys(log.hourlyData).slice(0, 3) : 'no hourly data'
        });
        
        return isToday && isUserLog && !isDeleted;
      });
      
      console.log('Found log:', userLog ? userLog.localId : 'none');
      return userLog || null;
    } catch (error) {
      console.error('Error getting current user log:', error);
      return null;
    }
  },

  // Save or update current user's log
  saveCurrentUserLog: async (logData) => {
    try {
      const db = await initDB();
      const userId = localStorage.getItem('userId');
      
      if (!logData.personId) {
        logData.personId = userId;
      }
      
      // Ensure we have a date
      if (!logData.date) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        logData.date = `${year}-${month}-${day}`;
      }
      
      // Update timestamps
      if (!logData.createdAt) {
        logData.createdAt = new Date().toISOString();
      }
      logData.updatedAt = new Date().toISOString();
      logData.syncStatus = 'pending';
      
      console.log('Saving to IndexedDB:', {
        localId: logData.localId,
        personId: logData.personId,
        date: logData.date,
        syncStatus: logData.syncStatus
      });
      
      // Save to IndexedDB
      await db.put(STORE_NAME, logData);
      console.log('Successfully saved to IndexedDB');
      
      return logData;
    } catch (error) {
      console.error('Error saving current user log:', error);
      throw error;
    }
  },
  // Get today's daily logs (kept for compatibility)
  getTodayLogs: async () => {
    // Helper function to get today's date in consistent format
    const getTodayDate = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = getTodayDate();
    
    try {
      // First try to get from IndexedDB if offline
      if (!navigator.onLine) {
        const db = await initDB();
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          console.log('DailyLogs store does not exist yet');
          return [];
        }
        
        const allLogs = await db.getAll(STORE_NAME);
        const todayLogs = allLogs.filter(log => {
          if (!log.date) return false;
          const logDate = log.date.split('T')[0];
          return logDate === today && !log.deleted;
        });
        return todayLogs;
      }
      
      // If online, fetch from server
      const response = await axiosInstance.get(`${URL}/daily-log`);
      const logs = response.data;
      
      // Save to IndexedDB for offline access
      try {
        const db = await initDB();
        if (db.objectStoreNames.contains(STORE_NAME)) {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          
          // Clear existing today's logs first to avoid duplicates
          const existingLogs = await tx.store.getAll();
          for (const existingLog of existingLogs) {
            if (existingLog.date && existingLog.date.split('T')[0] === today) {
              await tx.store.delete(existingLog.localId);
            }
          }
          
          // Add new logs with proper local ID
          for (const log of logs) {
            const logData = {
              ...log,
              localId: `server_${log.id}`,
              syncStatus: 'synced',
              date: log.date || today,
              // Add personId for compatibility with local search
              personId: log.createdBy?.id?.toString() || localStorage.getItem('userId')
            };
            console.log('Saving server log to IndexedDB:', logData);
            await tx.store.put(logData);
          }
          await tx.done;
        }
      } catch (idbError) {
        console.warn('Could not save logs to IndexedDB:', idbError);
      }
      
      return logs;
    } catch (error) {
      console.error('Error fetching logs:', error);
      
      // If error (network or otherwise), get from IndexedDB
      const db = await initDB();
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        console.log('DailyLogs store does not exist yet');
        return [];
      }
      
      const allLogs = await db.getAll(STORE_NAME);
      const todayLogs = allLogs.filter(log => {
        if (!log.date) return false;
        const logDate = log.date.split('T')[0];
        return logDate === today && !log.deleted;
      });
      return todayLogs;
    }
  },

  // Create new daily log
  createLog: async (data) => {
    // Helper function to get today's date in consistent format
    const getTodayDate = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const logData = {
      ...data,
      date: getTodayDate(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      syncStatus: 'pending'
    };

    try {
      if (navigator.onLine) {
        const response = await axiosInstance.post(`${URL}/daily-log`, data);
        const createdLog = response.data;
        
        // Save to IndexedDB with proper localId
        const db = await initDB();
        await db.put(STORE_NAME, { 
          ...createdLog, 
          localId: `server_${createdLog.id}`,
          syncStatus: 'synced',
          date: createdLog.date || getTodayDate()
        });
        
        return createdLog;
      }
    } catch (error) {
      if (!navigator.onLine || error.code === 'ERR_NETWORK') {
        // Save to IndexedDB with pending status
        const db = await initDB();
        const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        await db.put(STORE_NAME, { ...logData, localId });
        return { ...logData, localId };
      }
      throw error;
    }

    // If offline, save to IndexedDB
    const db = await initDB();
    const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await db.put(STORE_NAME, { ...logData, localId });
    return { ...logData, localId };
  },

  // Update daily log
  updateLog: async (id, data) => {
    const updateData = {
      ...data,
      updatedAt: new Date().toISOString(),
      syncStatus: 'pending'
    };

    try {
      // If it's a server ID and we're online, update on server
      if (navigator.onLine && typeof id === 'number' && !data.localId) {
        const response = await axiosInstance.put(`${URL}/daily-log/${id}`, {
          location: data.location,
          safetyAudit: data.safetyAudit,
          notes: data.notes,
          hourlyData: data.hourlyData
        });
        const updatedLog = response.data;
        
        // Try to update in IndexedDB if store exists
        try {
          const db = await initDB();
          if (db.objectStoreNames.contains(STORE_NAME)) {
            // Find the record by server ID
            const allRecords = await db.getAll(STORE_NAME);
            const existingRecord = allRecords.find(r => r.id === id);
            if (existingRecord) {
              await db.put(STORE_NAME, { ...updatedLog, localId: existingRecord.localId, syncStatus: 'synced' });
            }
          }
        } catch (idbError) {
          console.warn('Could not update IndexedDB:', idbError);
        }
        
        return updatedLog;
      }
    } catch (error) {
      console.error('Error updating on server:', error);
      // Continue to update locally
    }

    // Update in IndexedDB (for offline or local records)
    try {
      const db = await initDB();
      
      // Check if store exists
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        throw new Error('Daily logs store not found. Please refresh the page.');
      }
      
      // Handle both local and server records
      let recordKey = data.localId || id;
      
      // If it's a server ID, check if we have it stored with server_ prefix
      if (typeof id === 'number' && !data.localId) {
        const allRecords = await db.getAll(STORE_NAME);
        const serverRecord = allRecords.find(r => r.id === id || r.localId === `server_${id}`);
        if (serverRecord) {
          recordKey = serverRecord.localId;
        }
      }
      
      const existing = await db.get(STORE_NAME, recordKey);
      
      if (existing) {
        const updated = { ...existing, ...updateData };
        await db.put(STORE_NAME, updated);
        return updated;
      }
      
      throw new Error('Record not found');
    } catch (error) {
      console.error('Error updating locally:', error);
      throw error;
    }
  },

  // Delete daily log
  deleteLog: async (id) => {
    try {
      if (navigator.onLine && id && typeof id === 'number') {
        await axiosInstance.delete(`${URL}/daily-log/${id}`);
      }
      
      // Remove from IndexedDB
      const db = await initDB();
      await db.delete(STORE_NAME, id);
      
      return true;
    } catch (error) {
      if (!navigator.onLine || error.code === 'ERR_NETWORK') {
        // Mark as deleted in IndexedDB
        const db = await initDB();
        const existing = await db.get(STORE_NAME, id);
        if (existing) {
          await db.put(STORE_NAME, { ...existing, deleted: true, syncStatus: 'pending' });
        }
        return true;
      }
      throw error;
    }
  },

  // Get trainers from IndexedDB only
  getTrainers: async () => {
    try {
      const db = await initDB();
      
      // Check if trainers store exists
      if (!db.objectStoreNames.contains(TRAINER_STORE)) {
        console.log('Trainers store does not exist in IndexedDB');
        return [];
      }
      
      const trainers = await db.getAll(TRAINER_STORE);
      console.log('Loaded trainers from IndexedDB:', trainers.length);
      
      return trainers;
    } catch (error) {
      console.error('Error loading trainers from IndexedDB:', error);
      return [];
    }
  },

  // Get locations from jobs (already in IndexedDB)
  getLocations: async () => {
    try {
      const db = await initializeDB();
      
      // Check if jobs store exists
      if (!db.objectStoreNames.contains(JOB_STORE)) {
        console.log('Jobs store does not exist in IndexedDB');
        return [];
      }
      
      // Get unique locations from jobs
      const tx = db.transaction(JOB_STORE, 'readonly');
      const store = tx.objectStore(JOB_STORE);
      const jobs = await store.getAll();
      await tx.done;
      
      console.log('Total jobs in IndexedDB:', jobs.length);
      
      const locations = [...new Set(jobs
        .filter(job => job.location && job.location.trim() !== '')
        .map(job => job.location.trim())
      )].sort();
      
      console.log('Loaded locations from IndexedDB:', locations);
      return locations;
    } catch (error) {
      console.error('Error loading locations from IndexedDB:', error);
      return [];
    }
  },

  // Sync pending logs
  syncPendingLogs: async () => {
    if (!navigator.onLine) {
      return { synced: 0, failed: 0, message: 'Offline - sync will happen when online' };
    }

    const db = await initDB();
    
    // Check if store exists
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      return { synced: 0, failed: 0, message: 'Store not found' };
    }
    
    const allLogs = await db.getAll(STORE_NAME);
    const pendingLogs = allLogs.filter(log => log.syncStatus === 'pending');
    
    if (pendingLogs.length === 0) {
      return { synced: 0, failed: 0, message: 'No pending logs to sync' };
    }
    
    let synced = 0;
    let failed = 0;
    const errors = [];

    for (const log of pendingLogs) {
      try {
        if (log.deleted) {
          // If marked as deleted and has server ID, delete from server
          if (log.id && typeof log.id === 'number') {
            await axiosInstance.delete(`${URL}/daily-log/${log.id}`);
          }
          // Remove from IndexedDB
          await db.delete(STORE_NAME, log.localId);
          synced++;
        } else if (log.id && typeof log.id === 'number') {
          // Update existing log - only send hourlyData
          const response = await axiosInstance.put(`${URL}/daily-log/${log.id}`, {
            hourlyData: log.hourlyData
          });
          
          // Delete old record and add new one with server data
          await db.delete(STORE_NAME, log.localId);
          const serverLog = { 
            ...response.data, 
            localId: `server_${response.data.id}`, 
            syncStatus: 'synced',
            date: response.data.date || log.date,
            personId: response.data.createdBy?.id?.toString() || log.personId || localStorage.getItem('userId')
          };
          console.log('Updated server log, saving to IndexedDB:', serverLog);
          await db.put(STORE_NAME, serverLog);
          synced++;
        } else {
          // Create new log - only send hourlyData since backend uses current user
          try {
            const response = await axiosInstance.post(`${URL}/daily-log`, {
              hourlyData: log.hourlyData,
              date: log.date
            });
            
            // Delete local record and add new one with server ID
            await db.delete(STORE_NAME, log.localId);
            const serverLog = { 
              ...response.data, 
              localId: `server_${response.data.id}`, 
              syncStatus: 'synced',
              date: response.data.date || log.date,
              personId: response.data.createdBy?.id?.toString() || log.personId || localStorage.getItem('userId')
            };
            console.log('Created new log on server, saving to IndexedDB:', serverLog);
            await db.put(STORE_NAME, serverLog);
            synced++;
          } catch (createError) {
            // If we get a duplicate error, the log already exists on server
            if (createError.response && createError.response.status === 400) {
              console.log('Daily log already exists on server for this date');
              // Just mark as synced
              await db.put(STORE_NAME, { ...log, syncStatus: 'synced' });
              synced++;
            } else {
              throw createError;
            }
          }
        }
      } catch (error) {
        console.error('Error syncing log:', error);
        errors.push({ log, error: error.message });
        
        // Mark as failed if it's a permanent error (not network)
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          await db.put(STORE_NAME, { ...log, syncStatus: 'failed', syncError: error.message });
        }
        
        failed++;
      }
    }

    // Store last sync time
    localStorage.setItem('lastDailyLogSync', new Date().toISOString());
    
    return { 
      synced, 
      failed, 
      message: `Synced ${synced} logs${failed > 0 ? `, ${failed} failed` : ''}`,
      errors: errors.length > 0 ? errors : undefined
    };
  },

  // Get sync status
  getSyncStatus: async () => {
    try {
      const db = await initDB();
      
      // Check if store exists
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        return {
          pendingCount: 0,
          syncedCount: 0,
          localOnlyCount: 0,
          isOnline: navigator.onLine
        };
      }
      
      const allLogs = await db.getAll(STORE_NAME);
      
      // Helper function to get today's date in consistent format
      const getTodayDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const today = getTodayDate();
      const todayLogs = allLogs.filter(log => {
        if (!log.date) return false;
        const logDate = log.date.split('T')[0];
        return logDate === today && !log.deleted;
      });
      
      const pendingCount = todayLogs.filter(log => log.syncStatus === 'pending').length;
      const syncedCount = todayLogs.filter(log => log.syncStatus === 'synced' && log.id).length;
      const localOnlyCount = todayLogs.filter(log => !log.id || (log.localId && !log.id)).length;
      
      return {
        pendingCount,
        syncedCount,
        localOnlyCount,
        isOnline: navigator.onLine
      };
    } catch (error) {
      console.error('Error getting sync status:', error);
      return {
        pendingCount: 0,
        syncedCount: 0,
        localOnlyCount: 0,
        isOnline: navigator.onLine
      };
    }
  }
};

// Removed auto-sync when coming online
// Removed periodic sync check - only sync on manual save

// Removed getLastSyncTime method - no longer tracking sync time

export default dailyLogService;